// client/src/routes/app/utilities/$serviceId/index.tsx — SCR-UTIL-003 „Detaliu serviciu &
// consum" · MAINT-008 (04 §8.8, §6.5's DETAIL-TABS, §5.10; the file path is 04 §7's own tree).
//
// THIS IS THE DEEP-LINK TARGET. The list's rows, the „Citire nouă" flow's return, and any link
// naming a reading all land here; the route reads its id from the URL and fetches that service,
// so a link opened cold — a fresh tab, an e-mail, a notification — arrives on the right meter
// with every action available.
//
// THE TABS ARE A SEARCH PARAM, NOT CHILD ROUTES, and that is 04's own doing: §7's tree gives
// SCR-UTIL-003 exactly one file and §8.8's inventory carries three utilities screens, so a tab
// per route would add screens the inventory would then be wrong about. §5.10's requirement is
// that the active tab survives refresh and share, which a `<Link search>` satisfies exactly —
// each tab is a real link, Back is an undo, and a pasted URL opens where the sender was
// (D-1121).
//
// A PAYMENT A TENANT ANNOUNCED IS ANSWERED HERE. The list's „Validează plata" opens this screen
// on `?tab=facturi` — the „Plăți" tab — where each claim is drawn with its receipt and its two
// answers (D-2350, D-2351). A landlord who arrives on any other tab is told about the claim by a
// banner above the tabs, and the tab itself carries the count, so the answer is never more than
// one press away whichever way the meter was opened (D-2353).
//
// ALL OF IT IS WITHDRAWN FROM VIEW (D-2700). While `utilityPaymentsVisible()` answers `false`
// the screen has the three tabs it had before D-2205, no banner and no pending read, and a
// `?tab=facturi` written while the tab was on — a notification, a bookmark — opens on the
// consumption tab rather than on a tab the bar no longer offers.
//
// ONE PAYLOAD FEEDS THE HEADER. `GET /utility-services/{id}` carries the property, the
// provider, who pays, the last index and the organization's window; the tabs fetch only their
// own thing — the readings page, the consumption series — and nothing is asked for twice.

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UtilityServiceDetail } from '@/client';
import { Icon } from '@/components/icons';
import { DetailHeader } from '@/components/layout/DetailHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { utilityPaymentsVisible } from '@/config/utility-payments';
import { SummaryPanel, SummaryPill } from '@/components/motion/SummaryPanel';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Glyph } from '@/components/ui/Glyph';
import { MoneyText } from '@/components/ui/MoneyText';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { AdministrationNotice, usePropertyWrite } from '@/features/properties/administration';
import { BillsTab } from '@/features/utilities/BillsTab';
import { ConsumptionPanel } from '@/features/utilities/ConsumptionPanel';
import { ReadingHistory } from '@/features/utilities/ReadingHistory';
import { ServiceFacts } from '@/features/utilities/ServiceFacts';
import { UtilityFormDialog } from '@/features/utilities/UtilityFormDialog';
import { WhoPaysBadge } from '@/features/utilities/WhoPaysBadge';
import { useUtilityErrorMessage } from '@/features/utilities/errors';
import {
  useDeleteUtilityService,
  useServicePendingBills,
  useUtilityService,
} from '@/features/utilities/queries';
import {
  UTILITY_TABS,
  utilityDetailSearchSchema,
  type UtilityTab,
} from '@/features/utilities/search';
import { formatPeriod } from '@/features/rent/periods';
import { formatDate, formatQuantity } from '@/lib/format';
import { wireNumber, wireNumberOrNull } from '@/lib/wire';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/utilities/$serviceId/')({
  validateSearch: utilityDetailSearchSchema,
  component: UtilityDetailScreen,
});

function UtilityDetailScreen() {
  const { serviceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation();
  const service = useUtilityService(serviceId);
  // BR-COOWN-09 rule 5 (D-2460). Every write on this screen — a reading, the service itself, a
  // bill and its two answers on the „Plăți" tab — is a write on the meter's apartment, so the
  // header states who runs it where the actions were, ONCE, for every tab under it, and the tabs'
  // own controls fall away without repeating it. Open while the service is loading (D-1992).
  const { canWrite, blockedByAdministration, administratorName } = usePropertyWrite(
    service.data?.propertyId,
    'Operations.Write',
  );
  // Whether the „Plăți" tab and its banner are on screen at all (D-2700). Off, a `facturi`
  // arriving in the URL is read as the default tab.
  const payments = utilityPaymentsVisible();
  const tabs = payments ? UTILITY_TABS : UTILITY_TABS.filter((tab) => tab !== 'facturi');
  const activeTab: UtilityTab = !payments && search.tab === 'facturi' ? 'consum' : search.tab;
  // The claims waiting on this meter — the banner's and the tab's count. The „Plăți" tab reads
  // the same key, so opening it asks for nothing twice.
  const pending = useServicePendingBills(serviceId, payments);
  const pendingCount = pending.data?.length ?? 0;
  const [editing, setEditing] = useState(false);

  if (service.isPending) return <LoadingState rows={4} />;
  if (service.isError) {
    return <ErrorState error={service.error} onRetry={() => void service.refetch()} />;
  }

  const detail = service.data;

  return (
    <RequirePermission permission="Operations.Read">
      <DetailHeader
        backTo="/app/utilities"
        backLabel={t('utilities.detail.backToList')}
        glyph="bolt"
        title={`${t(`enums.UtilityType.${detail.utilityType}`)} · ${detail.providerName}`}
        subtitle={detail.propertyName}
        chips={
          <>
            <WhoPaysBadge whoPays={detail.whoPays} tenantSubmitsIndex={detail.tenantSubmitsIndex} />
            {detail.active ? null : (
              <span className="chip chip-neutral">{t('utilities.list.retired')}</span>
            )}
          </>
        }
        actions={
          canWrite ? (
            <>
              {detail.active ? (
                <Link
                  to="/app/utilities/$serviceId/readings/new"
                  params={{ serviceId }}
                  className="btn btn-primary btn-sm"
                >
                  {t('utilities.list.newReading')}
                </Link>
              ) : null}
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setEditing(true)}
              >
                <Icon name="cog" className="h-4 w-4" />
                {t('utilities.detail.edit')}
              </button>
              <DeleteAction serviceId={serviceId} providerName={detail.providerName} />
            </>
          ) : blockedByAdministration ? (
            <AdministrationNotice name={administratorName} />
          ) : undefined
        }
      />

      <MeterSummary service={detail} />

      {payments &&
      canWrite &&
      activeTab !== 'facturi' &&
      pending.data !== undefined &&
      pendingCount > 0 ? (
        <PendingBanner serviceId={serviceId} bills={pending.data} />
      ) : null}

      <nav className="tabs tabs-border mb-6" aria-label={t('utilities.detail.tabsLabel')}>
        {tabs.map((tab) => (
          <Link
            key={tab}
            to="/app/utilities/$serviceId"
            params={{ serviceId }}
            search={(previous) => ({ ...previous, tab })}
            // NOT `activeProps`: all three links point at the SAME route, so TanStack's own
            // notion of „active" is true for all three at once and the class would land on
            // every tab. The active tab here is a search param, so the comparison is one —
            // and `aria-current` is set from it too, since the stylesheet draws the underline
            // from that attribute (D-1842).
            className={`tab ${activeTab === tab ? 'tab-active' : ''}`}
            aria-current={activeTab === tab ? 'page' : undefined}
          >
            {tabLabel(tab, t)}
            {tab === 'facturi' && pendingCount > 0 ? (
              <>
                <span className="chip chip-warning ml-2 tabular-nums" aria-hidden="true">
                  {pendingCount}
                </span>
                <span className="sr-only">
                  {t('utilities.bills.waiting', { count: pendingCount })}
                </span>
              </>
            ) : null}
          </Link>
        ))}
      </nav>

      {activeTab === 'consum' ? <ConsumptionPanel serviceId={serviceId} /> : null}
      {activeTab === 'citiri' ? (
        <ReadingHistory
          serviceId={serviceId}
          unit={detail.measurementUnit}
          page={search.readingsPage}
          readingId={search.readingId}
          // „Modificați" and „Ștergeți" on the rows (D-3150, D-3151): the same answer the
          // header's verbs take, since a reading is a write on the meter's apartment.
          canWrite={canWrite}
          onPageChange={(page) => {
            void navigate({ search: (previous) => ({ ...previous, readingsPage: page }) });
          }}
        />
      ) : null}
      {activeTab === 'detalii' ? <ServiceFacts service={detail} /> : null}
      {activeTab === 'facturi' ? (
        <BillsTab
          service={detail}
          page={search.billsPage}
          onPageChange={(page) => {
            void navigate({ search: (previous) => ({ ...previous, billsPage: page }) });
          }}
        />
      ) : null}

      <UtilityFormDialog open={editing} onClose={() => setEditing(false)} service={detail} />
    </RequirePermission>
  );
}

/**
 * „Situația contorului" (D-3802): the summary panel of the page anatomy, between the header and
 * the tabs — the meter's last index as the headline, the day it was read, how many readings it
 * has, and the reading window the tenant is asked in. Every figure is the detail payload's; the
 * panel holds nothing to operate (the verbs stay in the header, the tabs under it).
 */
function MeterSummary({ service }: { service: UtilityServiceDetail }) {
  const { t } = useTranslation();
  const lastIndex = wireNumberOrNull(service.lastIndex);
  const readingWindow = service.indexWindow;

  return (
    <SummaryPanel title={t('utilities.meterSummary.title')} icon="bolt" compact className="mb-6">
      <p className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-3xl font-bold tracking-tight text-base-content tabular-nums">
          {lastIndex === null
            ? t('utilities.list.neverRead')
            : formatQuantity(lastIndex, service.measurementUnit)}
        </span>
        {lastIndex !== null && service.lastReadingDate != null ? (
          <span className="text-sm text-gray-500 tabular-nums">
            {t('utilities.meterSummary.readOn', { date: formatDate(service.lastReadingDate) })}
          </span>
        ) : null}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <SummaryPill caption={t('utilities.meterSummary.readings')}>
          {wireNumber(service.readingCount)}
        </SummaryPill>
        <SummaryPill caption={t('utilities.meterSummary.window')}>
          {readingWindow.enabled
            ? t('utilities.meterSummary.windowDays', {
                from: wireNumber(readingWindow.dayFrom),
                to: wireNumber(readingWindow.dayTo),
              })
            : t('utilities.meterSummary.windowOff')}
        </SummaryPill>
        <SummaryPill caption={t('utilities.meterSummary.unit')}>
          {service.measurementUnit}
        </SummaryPill>
      </div>
    </SummaryPanel>
  );
}

/**
 * „Chiriașul a anunțat plata" — above the tabs, on every tab but „Plăți" (D-2353).
 *
 * One claim is named in full — who, which month, how much — because that is the whole of what a
 * landlord needs to decide whether to look now; two or more are counted. The button is the
 * list's own verb and opens the tab where the claims are answered; it does not answer one here,
 * because the receipt is on that tab and nowhere on this one.
 */
function PendingBanner({
  serviceId,
  bills,
}: {
  serviceId: string;
  bills: NonNullable<ReturnType<typeof useServicePendingBills>['data']>;
}) {
  const { t } = useTranslation();
  const first = bills[0];
  if (first === undefined) return null;
  // Outside the sentence's own t() call: the i18n extractor does not read a key nested in
  // another call's options, and the gate would call this one an orphan.
  const tenant = first.tenantName ?? t('utilities.detail.pendingTenantFallback');

  return (
    // The pending card's own amber frame rather than `.alert-warning`, whose soft variant prints
    // amber text on amber — the pairing doc 04 §10.2 R4 refuses. The words stay body-coloured.
    <div
      className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning/60 bg-warning/5 px-4 py-3"
      role="status"
    >
      <p className="flex min-w-0 items-center gap-3 text-sm text-base-content">
        <Glyph icon="receipt" tone="warning" size="sm" />
        {bills.length === 1 ? (
          <span>
            {t('utilities.detail.pendingOne', {
              tenant,
              period: formatPeriod(
                { year: wireNumber(first.periodYear), month: wireNumber(first.periodMonth) },
                t,
              ),
            })}{' '}
            <MoneyText
              amount={wireNumber(first.amount)}
              currency={first.currency}
              className="font-semibold"
            />
          </span>
        ) : (
          <span>{t('utilities.detail.pendingMany', { count: bills.length })}</span>
        )}
      </p>
      <Link
        to="/app/utilities/$serviceId"
        params={{ serviceId }}
        search={(previous) => ({ ...previous, tab: 'facturi' as const })}
        className="btn btn-warning-soft btn-sm"
      >
        <Icon name="check-circle" className="h-4 w-4" />
        {t('utilities.list.validatePayment', { count: bills.length })}
      </Link>
    </div>
  );
}

/** The four labels, by literal: §9.6.3 admits one computed key shape and it is `enums.*`. */
function tabLabel(tab: UtilityTab, t: (key: string) => string): string {
  switch (tab) {
    case 'consum':
      return t('utilities.detail.tabs.consumption');
    case 'citiri':
      return t('utilities.detail.tabs.readings');
    case 'detalii':
      return t('utilities.detail.tabs.details');
    case 'facturi':
      return t('utilities.detail.tabs.bills');
    default: {
      const exhaustive: never = tab;
      return exhaustive;
    }
  }
}

function DeleteAction({ serviceId, providerName }: { serviceId: string; providerName: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remove = useDeleteUtilityService();
  const describe = useUtilityErrorMessage();
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const destroy = async () => {
    setFailure(null);
    try {
      await remove.mutateAsync({ path: { id: serviceId } });
      setConfirming(false);
      toast.success(t('utilities.detail.deleted'));
      await navigate({ to: '/app/utilities', search: { page: 1, pageSize: 25 } });
    } catch (error) {
      setFailure(describe(error));
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-outline btn-error btn-sm"
        onClick={() => setConfirming(true)}
      >
        {t('common.delete')}
      </button>

      {/* The endpoint's own summary, said to the person pressing the button: the deletion is
          logical and the readings survive it, „because the consumption history is the reason
          the utility existed". */}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void destroy()}
        title={t('utilities.detail.deleteTitle')}
        description={t('utilities.detail.deleteBody', { name: providerName })}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        pendingLabel={t('common.saving')}
      >
        {failure ? (
          <div className="alert alert-error text-sm" role="alert">
            {failure}
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
